import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderArrowDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Select, { StylesConfig } from "react-select";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import { getCompanyDocumentError } from "../../api/companyDocuments";
import {
  useCompanyDocumentBrowser,
  useCompanyDocumentDownloadUrl,
  useCompanyDocumentSearch,
  useDeleteCompanyDocumentItems,
  useMoveCompanyDocumentFiles,
  useQueueCompanyDocumentDownload,
  useRelatedCompanyDocuments,
} from "../../queries/companyDocuments";
import { useAuthStore } from "../../store";
import { useCanUserAccess } from "../../store/useRoleGrantStore";
import {
  CompanyDocumentBreadcrumb,
  CompanyDocumentDeleteItem,
  CompanyDocumentDownloadItem,
  CompanyDocumentFileTypeFilter,
  CompanyDocumentFolder,
  CompanyDocumentSearchFile,
  CompanyDocumentSearchFolder,
  CompanyDocumentSearchSortBy,
  CompanyDocumentSearchSortOrder,
  DocumentOrigin,
  GenericCompanyDocument,
  RelatedCompanyDocument,
  RelatedDocumentFilter,
} from "../../types/companyDocuments";
import {
  formatCompanyDocumentDate,
  formatCompanyDocumentSize,
  openCompanyDocumentDownload,
} from "../../utils/companyDocuments";
import DocumentFileTypeIcon from "./DocumentFileTypeIcon";
import DocumentFolderDialog from "./DocumentFolderDialog";
import DocumentUploadDialog from "./DocumentUploadDialog";

type ActiveTab = "company" | "other";
type UpdatedRangeFilter = "ALL" | "LAST_7_DAYS" | "LAST_30_DAYS" | "THIS_YEAR";
type FilterOption<Value extends string = string> = { value: Value; label: string };

type VaultSelection =
  | {
      kind: "FILE";
      id: string;
      name: string;
      origin: DocumentOrigin;
    }
  | {
      kind: "FOLDER";
      id: string;
      name: string;
    };

type DeleteTarget = {
  items: VaultSelection[];
};

const MAX_BUNDLE_FILE_COUNT = 20;
const SEARCH_PAGE_SIZE = 100;

const RELATED_FILTERS: FilterOption<RelatedDocumentFilter>[] =
  [
    { value: "ALL", label: "All sources" },
    { value: "TRANSACTION", label: "Transactions" },
    { value: "BOARD_MEETING", label: "Board meetings" },
    { value: "DIRECTOR", label: "Directors" },
  ];

const FILE_TYPE_FILTERS: FilterOption<CompanyDocumentFileTypeFilter>[] = [
  { value: "ALL", label: "All file types" },
  { value: "PDF", label: "PDFs" },
  { value: "SPREADSHEET", label: "Spreadsheets" },
  { value: "DOCUMENT", label: "Documents" },
  { value: "PRESENTATION", label: "Presentations" },
  { value: "IMAGE", label: "Images" },
  { value: "TEXT", label: "Text files" },
];

const SORT_OPTIONS: FilterOption<
  `${CompanyDocumentSearchSortBy}:${CompanyDocumentSearchSortOrder}`
>[] = [
  { value: "updatedAt:desc", label: "Recently updated" },
  { value: "updatedAt:asc", label: "Oldest updated" },
  { value: "name:asc", label: "Name A–Z" },
  { value: "name:desc", label: "Name Z–A" },
  { value: "size:desc", label: "Largest first" },
  { value: "size:asc", label: "Smallest first" },
];

const UPDATED_RANGE_FILTERS: FilterOption<UpdatedRangeFilter>[] = [
  { value: "ALL", label: "Any update date" },
  { value: "LAST_7_DAYS", label: "Updated in 7 days" },
  { value: "LAST_30_DAYS", label: "Updated in 30 days" },
  { value: "THIS_YEAR", label: "Updated this year" },
];

function filterSelectStyles<Value extends string>(
  withLeadingIcon = false
): StylesConfig<FilterOption<Value>, false> {
  return {
    control: (base, state) => ({
      ...base,
      minHeight: "2.5rem",
      height: "2.5rem",
      borderColor: state.isFocused ? "var(--primary, #2563eb)" : "#d1d5db",
      borderRadius: "0.375rem",
      boxShadow: state.isFocused
        ? "inset 0 0 0 1px var(--primary, #2563eb)"
        : "none",
      "&:hover": { borderColor: state.isFocused ? "var(--primary, #2563eb)" : "#9ca3af" },
    }),
    valueContainer: (base) => ({
      ...base,
      paddingLeft: withLeadingIcon ? "2rem" : "0.75rem",
      paddingRight: "0.25rem",
    }),
    indicatorsContainer: (base) => ({ ...base, height: "2.5rem" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0.5rem", color: "#6b7280" }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 50 }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.875rem",
      backgroundColor: state.isSelected ? "var(--primary, #2563eb)" : state.isFocused ? "#f3f4f6" : "white",
    }),
  };
}

function updatedFromForRange(range: UpdatedRangeFilter): string | undefined {
  if (range === "ALL") return undefined;
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  if (range === "LAST_7_DAYS") date.setDate(date.getDate() - 7);
  if (range === "LAST_30_DAYS") date.setDate(date.getDate() - 30);
  if (range === "THIS_YEAR") date.setMonth(0, 1);
  return date.toISOString();
}

function relatedDocumentMatchesFileType(
  document: RelatedCompanyDocument,
  fileType: CompanyDocumentFileTypeFilter
) {
  if (fileType === "ALL" || document.documentType === "LINK") return true;
  const extension = document.name.split(".").pop()?.toLowerCase();
  const matches: Record<Exclude<CompanyDocumentFileTypeFilter, "ALL">, string[]> = {
    PDF: ["pdf"],
    SPREADSHEET: ["xls", "xlsx", "xlsm", "csv"],
    DOCUMENT: ["doc", "docx"],
    PRESENTATION: ["ppt", "pptx"],
    IMAGE: ["gif", "jpeg", "jpg", "png", "webp"],
    TEXT: ["txt"],
  };
  return Boolean(extension && matches[fileType].includes(extension));
}

function fileSelection(
  file: Pick<GenericCompanyDocument, "id" | "name">,
  origin: DocumentOrigin = "GENERIC"
): VaultSelection {
  return { kind: "FILE", id: file.id, name: file.name, origin };
}

function folderSelection(
  folder: Pick<CompanyDocumentFolder, "id" | "name">
): VaultSelection {
  return { kind: "FOLDER", id: folder.id, name: folder.name };
}

function selectionKey(item: VaultSelection) {
  return item.kind === "FOLDER"
    ? `FOLDER:${item.id}`
    : `FILE:${item.origin}:${item.id}`;
}

function selectionToDeleteItem(
  item: VaultSelection
): CompanyDocumentDeleteItem | null {
  if (item.kind === "FOLDER") return { type: "FOLDER", id: item.id };
  if (item.origin === "GENERIC") return { type: "FILE", id: item.id };
  return null;
}

function breadcrumbLabel(
  breadcrumb: CompanyDocumentBreadcrumb[] | undefined,
  omitLast = false
) {
  const items = omitLast ? breadcrumb?.slice(0, -1) : breadcrumb;
  return items?.map((item) => item.name).join(" / ") || "Company Documents";
}

function isSearchFile(
  file: GenericCompanyDocument | CompanyDocumentSearchFile
): file is CompanyDocumentSearchFile {
  return "folder" in file;
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex h-full min-h-[12rem] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center">
      <FolderIcon className="h-9 w-9 text-gray-300" />
      <p className="mt-3 text-sm font-medium text-gray-700">{title}</p>
      <p className="mt-1 max-w-md text-sm text-gray-500">{detail}</p>
    </div>
  );
}

function SourceBadge({ source }: { source: RelatedCompanyDocument["source"] }) {
  const label =
    source === "BOARD_MEETING"
      ? "Board meeting"
      : source === "LEGACY_COMPANY"
      ? "Company"
      : source === "COMPANY_LINK"
      ? "Link"
      : source.charAt(0) + source.slice(1).toLowerCase().replace("_", " ");
  return (
    <span className="inline-flex max-w-full truncate rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
      {label}
    </span>
  );
}

function IconActionButton({
  label,
  disabled = false,
  onClick,
  tone = "default",
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  tone?: "default" | "danger";
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition disabled:cursor-not-allowed disabled:opacity-40 ${
        tone === "danger"
          ? "text-red-600 hover:bg-red-50"
          : "text-gray-500 hover:bg-primary/10 hover:text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function SelectAllCheckbox({
  items,
  selected,
  onChange,
  label = "Select visible items",
}: {
  items: VaultSelection[];
  selected: VaultSelection[];
  onChange: (checked: boolean) => void;
  label?: string;
}) {
  const selectedKeys = new Set(selected.map(selectionKey));
  const checked =
    items.length > 0 &&
    items.every((item) => selectedKeys.has(selectionKey(item)));
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={items.length === 0}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
    />
  );
}

function CompanyDocumentsTable({
  folders,
  files,
  showLocation,
  currentPath,
  selectedItems,
  selectedKeys,
  canUpdateDocuments,
  isDownloadBusy,
  isDeleteBusy,
  downloadingKey,
  onToggleSelection,
  onSelectVisible,
  onOpenFolder,
  onDownloadFile,
  onDeleteItems,
}: {
  folders: Array<CompanyDocumentFolder | CompanyDocumentSearchFolder>;
  files: Array<GenericCompanyDocument | CompanyDocumentSearchFile>;
  showLocation: boolean;
  currentPath: string;
  selectedItems: VaultSelection[];
  selectedKeys: Set<string>;
  canUpdateDocuments: boolean;
  isDownloadBusy: boolean;
  isDeleteBusy: boolean;
  downloadingKey: string | null;
  onToggleSelection: (item: VaultSelection) => void;
  onSelectVisible: (checked: boolean) => void;
  onOpenFolder: (folderId: string) => void;
  onDownloadFile: (item: CompanyDocumentDownloadItem) => void;
  onDeleteItems: (items: VaultSelection[]) => void;
}) {
  const selectableItems = files
    .filter((file) => file.downloadable)
    .map((file) => fileSelection(file));
  const hasSelectedFiles = selectedItems.some((item) => item.kind === "FILE");

  return (
    <table className="w-full min-w-[760px] table-fixed divide-y divide-gray-200 text-sm">
      <colgroup>
        <col className="w-12" />
        <col />
        {showLocation && <col className="w-44" />}
        <col className="w-40" />
        <col className="w-32" />
        <col className="w-24" />
      </colgroup>
      <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500 shadow-[0_1px_0_0_rgb(229_231_235)]">
        <tr>
          <th className="px-4 py-3">
            <SelectAllCheckbox
              items={[
                ...selectableItems,
                ...(selectableItems.length || !canUpdateDocuments
                  ? []
                  : folders.map(folderSelection)),
              ]}
              selected={selectedItems}
              onChange={onSelectVisible}
            />
          </th>
          <th className="px-3 py-3">Name</th>
          {showLocation && <th className="px-3 py-3">Location</th>}
          <th className="px-3 py-3">Size</th>
          <th className="px-3 py-3">Updated</th>
          <th className="px-3 py-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {folders.map((folder) => {
          const selection = folderSelection(folder);
          const location = showLocation
            ? breadcrumbLabel(
                "breadcrumb" in folder ? folder.breadcrumb : undefined,
                true
              )
            : currentPath;
          return (
            <tr key={`folder:${folder.id}`} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                {canUpdateDocuments && (
                  <input
                    type="checkbox"
                    aria-label={`Select folder ${folder.name}`}
                    checked={selectedKeys.has(selectionKey(selection))}
                    disabled={isDeleteBusy || hasSelectedFiles}
                    onChange={() => onToggleSelection(selection)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
                  />
                )}
              </td>
              <td className="px-3 py-3">
                <button
                  type="button"
                  onClick={() => onOpenFolder(folder.id)}
                  className="flex w-full min-w-0 items-center gap-2 text-left text-primary hover:underline"
                  title={folder.name}
                >
                  <FolderIcon className="h-5 w-5 shrink-0 fill-amber-100 text-amber-500" />
                  <span className="truncate font-medium">{folder.name}</span>
                </button>
              </td>
              {showLocation && (
                <td className="px-3 py-3 text-gray-500">
                  <span className="block truncate" title={location}>
                    {location}
                  </span>
                </td>
              )}
              <td className="px-3 py-3 text-gray-500">
                {folder.fileCount || 0} {folder.fileCount === 1 ? "file" : "files"}
                {" · "}
                {formatCompanyDocumentSize(folder.totalSize || "0")}
              </td>
              <td className="px-3 py-3 text-gray-500">
                {formatCompanyDocumentDate(folder.updatedAt)}
              </td>
              <td className="px-3 py-3">
                <div className="flex justify-end gap-1">
                  <IconActionButton
                    label={`Open folder ${folder.name}`}
                    disabled={isDeleteBusy}
                    onClick={() => onOpenFolder(folder.id)}
                  >
                    <FolderOpenIcon className="h-4 w-4" />
                  </IconActionButton>
                  {canUpdateDocuments && (
                    <IconActionButton
                      label={`Delete folder ${folder.name}`}
                      disabled={isDeleteBusy}
                      tone="danger"
                      onClick={() => onDeleteItems([selection])}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </IconActionButton>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
        {files.map((file) => {
          const selection = fileSelection(file);
          const downloadItem: CompanyDocumentDownloadItem = {
            origin: "GENERIC",
            id: file.id,
          };
          const key = `FILE:GENERIC:${file.id}`;
          const location = isSearchFile(file)
            ? breadcrumbLabel(file.folder?.breadcrumb)
            : currentPath;
          return (
            <tr key={`file:${file.id}`} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  aria-label={`Select ${file.name}`}
                  checked={selectedKeys.has(selectionKey(selection))}
                  disabled={!file.downloadable || isDeleteBusy}
                  onChange={() => onToggleSelection(selection)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                />
              </td>
              <td className="px-3 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <DocumentFileTypeIcon
                    fileName={file.name}
                    className="shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate font-medium text-gray-800"
                      title={file.name}
                    >
                      {file.name}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-gray-500 sm:hidden">
                      {formatCompanyDocumentSize(file.size)}
                      {file.uploadedBy?.name
                        ? ` · ${file.uploadedBy.name}`
                        : ""}
                    </p>
                  </div>
                </div>
              </td>
              {showLocation && (
                <td className="px-3 py-3 text-gray-500">
                  {isSearchFile(file) && file.folder ? (
                    <button
                      type="button"
                      onClick={() => onOpenFolder(file.folder!.id)}
                      className="block w-full truncate text-left hover:text-primary hover:underline"
                      title={location}
                    >
                      {location}
                    </button>
                  ) : (
                    <span className="block truncate" title={location}>
                      {location}
                    </span>
                  )}
                </td>
              )}
              <td className="px-3 py-3 text-gray-500">
                {formatCompanyDocumentSize(file.size)}
              </td>
              <td className="px-3 py-3 text-gray-500">
                {formatCompanyDocumentDate(file.updatedAt)}
              </td>
              <td className="px-3 py-3">
                <div className="flex justify-end gap-1">
                  <IconActionButton
                    label={`Download ${file.name}`}
                    disabled={
                      !file.downloadable || isDownloadBusy || isDeleteBusy
                    }
                    onClick={() => onDownloadFile(downloadItem)}
                  >
                    {downloadingKey === key ? (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    )}
                  </IconActionButton>
                  {canUpdateDocuments && (
                    <IconActionButton
                      label={`Delete ${file.name}`}
                      disabled={isDeleteBusy}
                      tone="danger"
                      onClick={() => onDeleteItems([selection])}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </IconActionButton>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RelatedDocumentsTable({
  documents,
  selectedItems,
  selectedKeys,
  isDownloadBusy,
  downloadingKey,
  onToggleSelection,
  onSelectVisible,
  onDownloadFile,
  onOpenLink,
}: {
  documents: RelatedCompanyDocument[];
  selectedItems: VaultSelection[];
  selectedKeys: Set<string>;
  isDownloadBusy: boolean;
  downloadingKey: string | null;
  onToggleSelection: (item: VaultSelection) => void;
  onSelectVisible: (checked: boolean) => void;
  onDownloadFile: (item: CompanyDocumentDownloadItem) => void;
  onOpenLink: (document: RelatedCompanyDocument) => void;
}) {
  return (
    <table className="w-full min-w-[820px] table-fixed divide-y divide-gray-200 text-sm">
      <colgroup>
        <col className="w-12" />
        <col />
        <col className="w-32" />
        <col className="w-44" />
        <col className="w-24" />
        <col className="w-32" />
        <col className="w-24" />
      </colgroup>
      <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500 shadow-[0_1px_0_0_rgb(229_231_235)]">
        <tr>
          <th className="px-4 py-3">
            <SelectAllCheckbox
              items={documents
                .filter(
                  (document) =>
                    document.documentType === "FILE" && document.downloadable
                )
                .map((document) => fileSelection(document, "EXISTING"))}
              selected={selectedItems}
              onChange={onSelectVisible}
            />
          </th>
          <th className="px-3 py-3">Name</th>
          <th className="px-3 py-3">Source</th>
          <th className="px-3 py-3">Context</th>
          <th className="px-3 py-3">Size</th>
          <th className="px-3 py-3">Updated</th>
          <th className="px-3 py-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {documents.map((document) => {
          const selection = fileSelection(document, "EXISTING");
          const selectable =
            document.documentType === "FILE" && document.downloadable;
          const key = `FILE:EXISTING:${document.id}`;
          return (
            <tr key={document.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  aria-label={`Select ${document.name}`}
                  checked={selectedKeys.has(selectionKey(selection))}
                  disabled={!selectable}
                  onChange={() => onToggleSelection(selection)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                />
              </td>
              <td className="px-3 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  {document.documentType === "LINK" ? (
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 shrink-0 text-gray-400" />
                  ) : (
                    <DocumentFileTypeIcon
                      fileName={document.name}
                      className="shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <p
                        className="truncate font-medium text-gray-800"
                        title={document.name}
                      >
                        {document.name}
                      </p>
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        Read-only
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-500 sm:hidden">
                      {formatCompanyDocumentSize(document.size)}
                    </p>
                  </div>
                </div>
              </td>
              <td className="px-3 py-3">
                <SourceBadge source={document.source} />
              </td>
              <td className="px-3 py-3 text-gray-500">
                <span
                  className="block truncate"
                  title={document.contextLabel || "—"}
                >
                  {document.contextLabel || "—"}
                </span>
              </td>
              <td className="px-3 py-3 text-gray-500">
                {formatCompanyDocumentSize(document.size)}
              </td>
              <td className="px-3 py-3 text-gray-500">
                {formatCompanyDocumentDate(document.updatedAt)}
              </td>
              <td className="px-3 py-3">
                <div className="flex justify-end gap-1">
                  {document.documentType === "LINK" ? (
                    <IconActionButton
                      label={`Open ${document.name}`}
                      onClick={() => onOpenLink(document)}
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </IconActionButton>
                  ) : (
                    <IconActionButton
                      label={`Download ${document.name}`}
                      disabled={!selectable || isDownloadBusy}
                      onClick={() =>
                        onDownloadFile({ origin: "EXISTING", id: document.id })
                      }
                    >
                      {downloadingKey === key ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      )}
                    </IconActionButton>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DeleteConfirmationDialog({
  target,
  isDeleting,
  onClose,
  onConfirm,
}: {
  target: DeleteTarget | null;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const fileCount =
    target?.items.filter((item) => item.kind === "FILE").length || 0;
  const folderCount =
    target?.items.filter((item) => item.kind === "FOLDER").length || 0;
  const itemCount = target?.items.length || 0;
  const itemLabel = [
    fileCount ? `${fileCount} ${fileCount === 1 ? "file" : "files"}` : "",
    folderCount
      ? `${folderCount} ${folderCount === 1 ? "folder" : "folders"}`
      : "",
  ]
    .filter(Boolean)
    .join(" and ");
  const preview = target?.items.slice(0, 3) || [];

  return (
    <Transition.Root show={Boolean(target)} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={isDeleting ? () => undefined : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/30" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                  </div>
                  <Dialog.Title className="mt-4 text-base font-semibold text-gray-900">
                    Delete {itemLabel || "selected items"}?
                  </Dialog.Title>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    This removes the selected items from the company file
                    manager. This action cannot be undone.
                    {folderCount
                      ? " Any files and subfolders inside selected folders will also be removed."
                      : ""}
                  </p>
                  {preview.length > 0 && (
                    <ul className="mt-4 space-y-1 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
                      {preview.map((item) => (
                        <li
                          key={selectionKey(item)}
                          className="truncate"
                          title={item.name}
                        >
                          {item.name}
                        </li>
                      ))}
                      {itemCount > preview.length && (
                        <li className="text-xs text-gray-500">
                          and {itemCount - preview.length} more
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isDeleting}
                    className="rounded-sm border border-primary px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isDeleting}
                    className="inline-flex items-center gap-1.5 rounded-sm bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isDeleting && (
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    )}
                    Delete
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function DocumentVoultView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = useAuthStore((state) => state.companyId || "");
  const canUpdateDocuments = useCanUserAccess("update", "siteSettings");
  const [activeTab, setActiveTab] = useState<ActiveTab>("company");
  const [relatedFilter, setRelatedFilter] =
    useState<RelatedDocumentFilter>("ALL");
  const [relatedSearchText, setRelatedSearchText] = useState("");
  const [debouncedRelatedSearchText] = useDebounce(relatedSearchText, 300);
  const [relatedFileTypeFilter, setRelatedFileTypeFilter] =
    useState<CompanyDocumentFileTypeFilter>("ALL");
  const [relatedUpdatedRangeFilter, setRelatedUpdatedRangeFilter] =
    useState<UpdatedRangeFilter>("ALL");
  const [relatedSortBy, setRelatedSortBy] =
    useState<CompanyDocumentSearchSortBy>("updatedAt");
  const [relatedSortOrder, setRelatedSortOrder] =
    useState<CompanyDocumentSearchSortOrder>("desc");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const [fileTypeFilter, setFileTypeFilter] =
    useState<CompanyDocumentFileTypeFilter>("ALL");
  const [updatedRangeFilter, setUpdatedRangeFilter] =
    useState<UpdatedRangeFilter>("ALL");
  const [sortBy, setSortBy] =
    useState<CompanyDocumentSearchSortBy>("updatedAt");
  const [sortOrder, setSortOrder] =
    useState<CompanyDocumentSearchSortOrder>("desc");
  const [searchPage, setSearchPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState<VaultSelection[]>([]);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  const folderId = searchParams.get("folderId") || null;
  const normalizedSearchText = debouncedSearchText.trim();
  const isCompanySearchMode =
    Boolean(normalizedSearchText) ||
    fileTypeFilter !== "ALL" ||
    updatedRangeFilter !== "ALL" ||
    sortBy !== "updatedAt" ||
    sortOrder !== "desc";
  const searchRequest = useMemo(
    () => ({
      q: normalizedSearchText || undefined,
      fileType: fileTypeFilter,
      updatedFrom: updatedFromForRange(updatedRangeFilter),
      sortBy,
      sortOrder,
      page: searchPage,
      pageSize: SEARCH_PAGE_SIZE,
    }),
    [
      fileTypeFilter,
      normalizedSearchText,
      searchPage,
      sortBy,
      sortOrder,
      updatedRangeFilter,
    ]
  );

  const browserQuery = useCompanyDocumentBrowser(
    activeTab === "company" ? companyId : "",
    folderId
  );
  const searchQuery = useCompanyDocumentSearch(
    activeTab === "company" ? companyId : "",
    searchRequest,
    activeTab === "company" && isCompanySearchMode
  );
  const relatedQuery = useRelatedCompanyDocuments(
    activeTab === "other" ? companyId : "",
    relatedFilter
  );
  const singleDownloadMutation = useCompanyDocumentDownloadUrl();
  const bundleDownloadMutation = useQueueCompanyDocumentDownload();
  const deleteItemsMutation = useDeleteCompanyDocumentItems(companyId);
  const moveFilesMutation = useMoveCompanyDocumentFiles(companyId);

  useEffect(() => {
    setSelectedItems([]);
  }, [activeTab, companyId]);

  useEffect(() => {
    setSearchPage(1);
  }, [
    fileTypeFilter,
    normalizedSearchText,
    sortBy,
    sortOrder,
    updatedRangeFilter,
  ]);

  const browser = browserQuery.data;
  const breadcrumb = browser?.breadcrumb || [
    { id: null, name: "Company Documents" },
  ];
  const targetBreadcrumb = breadcrumb.map((item) => item.name);
  const currentPath = breadcrumbLabel(breadcrumb);
  const searchResults = searchQuery.data;
  const companyFolders = isCompanySearchMode
    ? searchResults?.folders || []
    : browser?.folders || [];
  const companyFiles = isCompanySearchMode
    ? searchResults?.files || []
    : browser?.files || [];
  const companyLoading = isCompanySearchMode
    ? searchQuery.isLoading
    : browserQuery.isLoading;
  const companyError = isCompanySearchMode
    ? searchQuery.isError
      ? searchQuery.error
      : null
    : browserQuery.isError
    ? browserQuery.error
    : null;
  const relatedDocuments = relatedQuery.data || [];
  const filteredRelatedDocuments = useMemo(() => {
    const search = debouncedRelatedSearchText.trim().toLowerCase();
    const updatedFrom = updatedFromForRange(relatedUpdatedRangeFilter);
    return relatedDocuments
      .filter((document) => {
        const searchable = [document.name, document.sourceLabel, document.contextLabel]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return (
          (!search || searchable.includes(search)) &&
          relatedDocumentMatchesFileType(document, relatedFileTypeFilter) &&
          (!updatedFrom || new Date(document.updatedAt) >= new Date(updatedFrom))
        );
      })
      .sort((left, right) => {
        const leftValue = relatedSortBy === "size"
          ? Number(left.size || 0)
          : relatedSortBy === "name"
          ? left.name
          : new Date(left.updatedAt).getTime();
        const rightValue = relatedSortBy === "size"
          ? Number(right.size || 0)
          : relatedSortBy === "name"
          ? right.name
          : new Date(right.updatedAt).getTime();
        const comparison = typeof leftValue === "string"
          ? leftValue.localeCompare(rightValue as string)
          : leftValue - (rightValue as number);
        return relatedSortOrder === "asc" ? comparison : -comparison;
      });
  }, [
    debouncedRelatedSearchText,
    relatedDocuments,
    relatedFileTypeFilter,
    relatedSortBy,
    relatedSortOrder,
    relatedUpdatedRangeFilter,
  ]);

  const visibleCompanySelections = useMemo(
    () => [
      ...(canUpdateDocuments ? companyFolders.map(folderSelection) : []),
      ...companyFiles
        .filter((file) => file.downloadable)
        .map((file) => fileSelection(file)),
    ],
    [canUpdateDocuments, companyFiles, companyFolders]
  );
  const visibleRelatedSelections = useMemo(
    () =>
      filteredRelatedDocuments
        .filter(
          (document) =>
            document.documentType === "FILE" && document.downloadable
        )
        .map((document) => fileSelection(document, "EXISTING")),
    [filteredRelatedDocuments]
  );
  const visibleSelections =
    activeTab === "company"
      ? visibleCompanySelections
      : visibleRelatedSelections;
  const selectedKeys = useMemo(
    () => new Set(selectedItems.map(selectionKey)),
    [selectedItems]
  );
  const selectedDownloadItems = useMemo(
    () =>
      selectedItems.flatMap((item) =>
        item.kind === "FILE" ? [{ origin: item.origin, id: item.id }] : []
      ),
    [selectedItems]
  );
  const selectedDeleteItems = useMemo(
    () =>
      canUpdateDocuments
        ? selectedItems.flatMap((item) => {
            const deleteItem = selectionToDeleteItem(item);
            return deleteItem ? [deleteItem] : [];
          })
        : [],
    [canUpdateDocuments, selectedItems]
  );
  const selectedMovableFileIds = useMemo(
    () =>
      selectedItems.flatMap((item) =>
        item.kind === "FILE" && item.origin === "GENERIC" ? [item.id] : []
      ),
    [selectedItems]
  );
  const isDownloadBusy =
    Boolean(downloadingKey) ||
    singleDownloadMutation.isLoading ||
    bundleDownloadMutation.isLoading;
  const isDeleteBusy = deleteItemsMutation.isLoading;
  const isMoveBusy = moveFilesMutation.isLoading;

  const navigateToFolder = (nextFolderId: string | null) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextFolderId) nextParams.set("folderId", nextFolderId);
    else nextParams.delete("folderId");
    setSearchParams(nextParams);
  };

  const openFolder = (nextFolderId: string) => {
    setSearchText("");
    setFileTypeFilter("ALL");
    setUpdatedRangeFilter("ALL");
    setSortBy("updatedAt");
    setSortOrder("desc");
    setSearchPage(1);
    navigateToFolder(nextFolderId);
  };

  const toggleSelection = (item: VaultSelection) => {
    const key = selectionKey(item);
    setSelectedItems((current) => {
      const isSelected = current.some(
        (selected) => selectionKey(selected) === key
      );
      if (isSelected) {
        return current.filter((selected) => selectionKey(selected) !== key);
      }
      if (item.kind === "FOLDER" && current.some((selected) => selected.kind === "FILE")) {
        return current;
      }
      return item.kind === "FILE"
        ? [...current.filter((selected) => selected.kind === "FILE"), item]
        : [...current, item];
    });
  };

  const selectVisible = (checked: boolean) => {
    const visibleKeys = new Set(visibleSelections.map(selectionKey));
    setSelectedItems((current) => {
      if (!checked) {
        return current.filter((item) => !visibleKeys.has(selectionKey(item)));
      }
      const visibleFiles = visibleSelections.filter(
        (item) => item.kind === "FILE"
      );
      const nextVisibleItems = visibleFiles.length
        ? visibleFiles
        : current.some((item) => item.kind === "FILE")
        ? []
        : visibleSelections;
      const baseItems = visibleFiles.length
        ? current.filter((item) => item.kind === "FILE")
        : current;
      const existingKeys = new Set(baseItems.map(selectionKey));
      return [
        ...baseItems,
        ...nextVisibleItems.filter(
          (item) => !existingKeys.has(selectionKey(item))
        ),
      ];
    });
  };

  const downloadOne = async (item: CompanyDocumentDownloadItem) => {
    if (isDownloadBusy || isDeleteBusy) return;
    const key = `FILE:${item.origin}:${item.id}`;
    setDownloadingKey(key);
    try {
      const response = await singleDownloadMutation.mutateAsync(item);
      const downloadUrl = response.downloadUrl || response.url;
      if (!downloadUrl) throw new Error("The download link was not returned.");
      openCompanyDocumentDownload(downloadUrl);
    } catch (error) {
      toast(getCompanyDocumentError(error), { type: "error", autoClose: 3000 });
    } finally {
      setDownloadingKey(null);
    }
  };

  const downloadSelected = async () => {
    if (!selectedDownloadItems.length || isDownloadBusy || isDeleteBusy) return;
    if (selectedDownloadItems.length === 1) {
      await downloadOne(selectedDownloadItems[0]);
      return;
    }
    if (selectedDownloadItems.length > MAX_BUNDLE_FILE_COUNT) {
      toast(
        `Select no more than ${MAX_BUNDLE_FILE_COUNT} files for one ZIP download.`,
        { type: "error", autoClose: 3500 }
      );
      return;
    }
    try {
      await bundleDownloadMutation.mutateAsync({
        items: selectedDownloadItems,
      });
      setSelectedItems([]);
      toast(
        "Your ZIP is being prepared. We will notify you here and by email when it is ready. It will be available for download for the next 24 hours.",
        { type: "success", autoClose: 4500 }
      );
    } catch (error) {
      toast(getCompanyDocumentError(error), { type: "error", autoClose: 3500 });
    }
  };

  const moveSelectedFiles = async (destinationFolderId: string | null) => {
    if (!selectedMovableFileIds.length || isMoveBusy) return;
    try {
      await moveFilesMutation.mutateAsync({
        fileIds: selectedMovableFileIds,
        folderId: destinationFolderId,
      });
      setSelectedItems([]);
      setMoveDialogOpen(false);
      toast("Files moved.", { type: "success", autoClose: 2000 });
    } catch (error) {
      toast(getCompanyDocumentError(error), { type: "error", autoClose: 3500 });
    }
  };

  const requestDelete = (items: VaultSelection[]) => {
    const deletableItems = items.filter((item) => selectionToDeleteItem(item));
    if (!deletableItems.length) return;
    setDeleteTarget({ items: deletableItems });
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleteBusy) return;
    const items = deleteTarget.items.flatMap((item) => {
      const deleteItem = selectionToDeleteItem(item);
      return deleteItem ? [deleteItem] : [];
    });
    if (!items.length) return;

    try {
      const response = await deleteItemsMutation.mutateAsync({ items });
      const { fileCount, folderCount } = response.deleted;
      const deletedLabel = [
        fileCount ? `${fileCount} ${fileCount === 1 ? "file" : "files"}` : "",
        folderCount
          ? `${folderCount} ${folderCount === 1 ? "folder" : "folders"}`
          : "",
      ]
        .filter(Boolean)
        .join(" and ");
      setSelectedItems([]);
      setDeleteTarget(null);
      if (items.some((item) => item.type === "FOLDER")) navigateToFolder(null);
      toast(`${deletedLabel || "Selected items"} deleted.`, {
        type: "success",
        autoClose: 3000,
      });
    } catch (error) {
      toast(getCompanyDocumentError(error), { type: "error", autoClose: 3500 });
    }
  };

  const openRelatedLink = (document: RelatedCompanyDocument) => {
    const url = document.url || document.externalUrl;
    if (!url) {
      toast("This link is unavailable.", { type: "error", autoClose: 2500 });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const clearCompanySearch = () => {
    setSearchText("");
    setFileTypeFilter("ALL");
    setUpdatedRangeFilter("ALL");
    setSortBy("updatedAt");
    setSortOrder("desc");
    setSearchPage(1);
  };

  const updateSort = (value: string) => {
    const [nextSortBy, nextSortOrder] = value.split(":") as [
      CompanyDocumentSearchSortBy,
      CompanyDocumentSearchSortOrder
    ];
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setSearchPage(1);
  };

  const reloadCurrentFolder = () => {
    browserQuery.refetch().catch(() => undefined);
    if (isCompanySearchMode) searchQuery.refetch().catch(() => undefined);
  };

  return (
    <div className="flex min-h-0 flex-col pb-8 lg:h-[calc(100dvh-13.5rem)] lg:pb-0">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex min-h-[54px] shrink-0 flex-col border-b border-gray-200 px-4 sm:flex-row sm:items-stretch sm:justify-between sm:px-6">
          <div
            className="flex items-end gap-6"
            role="tablist"
            aria-label="Document views"
          >
            <button
              type="button"
              onClick={() => setActiveTab("company")}
              role="tab"
              aria-selected={activeTab === "company"}
              className={`border-b-2 py-4 text-sm font-medium leading-5 ${
                activeTab === "company"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              Company files
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("other")}
              role="tab"
              aria-selected={activeTab === "other"}
              className={`border-b-2 py-4 text-sm font-medium leading-5 ${
                activeTab === "other"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              Other documents
            </button>
          </div>
          {activeTab === "company" && canUpdateDocuments && (
            <div className="flex flex-wrap items-center gap-2 py-3 sm:py-0">
              <button
                type="button"
                onClick={() => setFolderDialogOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" />
                New folder
              </button>
              <button
                type="button"
                onClick={() => setUploadDialogOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                <ArrowDownTrayIcon className="h-4 w-4 rotate-180" />
                Upload files
              </button>
            </div>
          )}
        </div>

        {activeTab === "company" ? (
          <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">
            <div className="shrink-0">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h1 className="text-base font-semibold text-gray-800">
                    Company files
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Search every folder, select files across folders, and manage
                    your company document library.
                  </p>
                </div>
                {isCompanySearchMode && searchResults && (
                  <p className="text-sm text-gray-500">
                    {searchResults.pagination.total}{" "}
                    {searchResults.pagination.total === 1 ? "file" : "files"}{" "}
                    found
                  </p>
                )}
              </div>

              <div className="mt-4 flex flex-col gap-2 lg:flex-row lg:items-stretch">
                <label className="relative block min-w-0 flex-1">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search files and folders across the company"
                    aria-label="Search files and folders"
                    className="h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm text-gray-700 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  {searchText && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      title="Clear search"
                      onClick={() => setSearchText("")}
                      className="absolute right-2 top-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </label>
                <div className="flex flex-wrap items-stretch gap-2">
                  <div className="relative w-44 shrink-0">
                    <FunnelIcon className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Select
                      value={
                        FILE_TYPE_FILTERS.find(
                          (filter) => filter.value === fileTypeFilter
                        ) || null
                      }
                      onChange={(option) => {
                        if (!option) return;
                        setFileTypeFilter(option.value);
                        setSearchPage(1);
                      }}
                      aria-label="Filter by file type"
                      options={FILE_TYPE_FILTERS}
                      styles={filterSelectStyles<CompanyDocumentFileTypeFilter>(true)}
                      isSearchable={false}
                    />
                  </div>
                  <Select
                    value={
                      UPDATED_RANGE_FILTERS.find(
                        (filter) => filter.value === updatedRangeFilter
                      ) || null
                    }
                    onChange={(option) => {
                      if (!option) return;
                      setUpdatedRangeFilter(option.value);
                      setSearchPage(1);
                    }}
                    aria-label="Filter by update date"
                    options={UPDATED_RANGE_FILTERS}
                    styles={filterSelectStyles<UpdatedRangeFilter>()}
                    className="w-44 shrink-0"
                    isSearchable={false}
                  />
                  <Select
                    value={SORT_OPTIONS.find((option) => option.value === `${sortBy}:${sortOrder}`) || null}
                    onChange={(option) => {
                      if (option) updateSort(option.value);
                    }}
                    aria-label="Sort files"
                    options={SORT_OPTIONS}
                    styles={filterSelectStyles<`${CompanyDocumentSearchSortBy}:${CompanyDocumentSearchSortOrder}`>()}
                    className="w-44 shrink-0"
                    isSearchable={false}
                  />
                  {isCompanySearchMode && (
                    <button
                      type="button"
                      onClick={clearCompanySearch}
                      aria-label="Clear filters"
                      title="Clear filters"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              {isCompanySearchMode ? (
                <p className="mt-3 text-xs text-gray-500">
                  Results include files and folders from across the company.
                  Your selected files stay selected while you move between
                  folders or search results.
                </p>
              ) : (
                <nav
                  className="mt-4 flex flex-wrap items-center gap-1 text-sm"
                  aria-label="Breadcrumb"
                >
                  {breadcrumb.map((item, index) => (
                    <span
                      key={item.id || "root"}
                      className="flex min-w-0 items-center gap-1"
                    >
                      {index > 0 && (
                        <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <button
                        type="button"
                        onClick={() => navigateToFolder(item.id)}
                        className={`max-w-[15rem] truncate rounded px-1 py-0.5 ${
                          index === breadcrumb.length - 1
                            ? "font-medium text-gray-800"
                            : "text-gray-500 hover:text-primary hover:underline"
                        }`}
                        title={item.name}
                      >
                        {item.name}
                      </button>
                    </span>
                  ))}
                </nav>
              )}
            </div>

            <div className="mt-4 flex min-h-0 flex-1 flex-col">
              {companyLoading ? (
                <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
                  <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                  {isCompanySearchMode ? "Searching files…" : "Loading files…"}
                </div>
              ) : companyError ? (
                <ErrorState message={getCompanyDocumentError(companyError)} />
              ) : !companyFolders.length && !companyFiles.length ? (
                <EmptyState
                  title={
                    isCompanySearchMode
                      ? "No matching files or folders"
                      : "No files or folders yet"
                  }
                  detail={
                    isCompanySearchMode
                      ? "Try a different search term or clear a filter."
                      : canUpdateDocuments
                      ? "Create a folder or upload files to start building your document library."
                      : "Files and folders shared by your team will appear here."
                  }
                />
              ) : (
                <>
                  <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200">
                    <CompanyDocumentsTable
                      folders={companyFolders}
                      files={companyFiles}
                      showLocation={isCompanySearchMode}
                      currentPath={currentPath}
                      selectedItems={selectedItems}
                      selectedKeys={selectedKeys}
                      canUpdateDocuments={canUpdateDocuments}
                      isDownloadBusy={isDownloadBusy}
                      isDeleteBusy={isDeleteBusy}
                      downloadingKey={downloadingKey}
                      onToggleSelection={toggleSelection}
                      onSelectVisible={selectVisible}
                      onOpenFolder={openFolder}
                      onDownloadFile={(item) => {
                        downloadOne(item).catch(() => undefined);
                      }}
                      onDeleteItems={requestDelete}
                    />
                  </div>
                  {isCompanySearchMode &&
                    searchResults &&
                    searchResults.pagination.totalPages > 1 && (
                      <div className="mt-3 flex shrink-0 items-center justify-between text-sm text-gray-500">
                        <span>
                          Page {searchResults.pagination.page} of{" "}
                          {searchResults.pagination.totalPages}
                        </span>
                        <div className="flex items-center gap-1">
                          <IconActionButton
                            label="Previous page"
                            disabled={
                              searchResults.pagination.page <= 1 ||
                              searchQuery.isFetching
                            }
                            onClick={() =>
                              setSearchPage((page) => Math.max(page - 1, 1))
                            }
                          >
                            <ChevronLeftIcon className="h-4 w-4" />
                          </IconActionButton>
                          <IconActionButton
                            label="Next page"
                            disabled={
                              searchResults.pagination.page >=
                                searchResults.pagination.totalPages ||
                              searchQuery.isFetching
                            }
                            onClick={() => setSearchPage((page) => page + 1)}
                          >
                            <ChevronRightIcon className="h-4 w-4" />
                          </IconActionButton>
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">
            <div className="mb-5 flex shrink-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-base font-semibold text-gray-800">
                  Other documents
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Files from transactions, board meetings, directors, and
                  onboarding are view-only here.
                </p>
              </div>
              <Select
                value={RELATED_FILTERS.find((filter) => filter.value === relatedFilter) || null}
                onChange={(option) => {
                  if (option) setRelatedFilter(option.value);
                }}
                aria-label="Filter other documents by source"
                options={RELATED_FILTERS}
                styles={filterSelectStyles<RelatedDocumentFilter>()}
                className="w-44 shrink-0"
                isSearchable={false}
              />
            </div>
            <div className="mb-4 flex shrink-0 flex-col gap-2 lg:flex-row lg:items-stretch">
              <label className="relative block min-w-0 flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={relatedSearchText}
                  onChange={(event) => setRelatedSearchText(event.target.value)}
                  placeholder="Search other documents"
                  aria-label="Search other documents"
                  className="h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm text-gray-700 outline-none placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                />
                {relatedSearchText && (
                  <button
                    type="button"
                    aria-label="Clear other document search"
                    onClick={() => setRelatedSearchText("")}
                    className="absolute right-2 top-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </label>
              <div className="flex flex-wrap items-stretch gap-2">
                <div className="relative w-44 shrink-0">
                  <FunnelIcon className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Select
                    value={
                      FILE_TYPE_FILTERS.find(
                        (filter) => filter.value === relatedFileTypeFilter
                      ) || null
                    }
                    onChange={(option) => option && setRelatedFileTypeFilter(option.value)}
                    aria-label="Filter other documents by file type"
                    options={FILE_TYPE_FILTERS}
                    styles={filterSelectStyles<CompanyDocumentFileTypeFilter>(true)}
                    isSearchable={false}
                  />
                </div>
                <Select
                  value={
                    UPDATED_RANGE_FILTERS.find(
                      (filter) => filter.value === relatedUpdatedRangeFilter
                    ) || null
                  }
                  onChange={(option) => option && setRelatedUpdatedRangeFilter(option.value)}
                  aria-label="Filter other documents by update date"
                  options={UPDATED_RANGE_FILTERS}
                  styles={filterSelectStyles<UpdatedRangeFilter>()}
                  className="w-44 shrink-0"
                  isSearchable={false}
                />
                <Select
                  value={SORT_OPTIONS.find((option) => option.value === `${relatedSortBy}:${relatedSortOrder}`) || null}
                  onChange={(option) => {
                    if (!option) return;
                    const [sortBy, sortOrder] = option.value.split(":") as [CompanyDocumentSearchSortBy, CompanyDocumentSearchSortOrder];
                    setRelatedSortBy(sortBy);
                    setRelatedSortOrder(sortOrder);
                  }}
                  aria-label="Sort other documents"
                  options={SORT_OPTIONS}
                  styles={filterSelectStyles<`${CompanyDocumentSearchSortBy}:${CompanyDocumentSearchSortOrder}`>()}
                  className="w-44 shrink-0"
                  isSearchable={false}
                />
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              {relatedQuery.isLoading ? (
                <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
                  <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
                  Loading documents…
                </div>
              ) : relatedQuery.isError ? (
                <ErrorState
                  message={getCompanyDocumentError(relatedQuery.error)}
                />
              ) : filteredRelatedDocuments.length === 0 ? (
                <EmptyState
                  title="No matching documents found"
                  detail="Try a different search term or filter."
                />
              ) : (
                <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200">
                  <RelatedDocumentsTable
                    documents={filteredRelatedDocuments}
                    selectedItems={selectedItems}
                    selectedKeys={selectedKeys}
                    isDownloadBusy={isDownloadBusy}
                    downloadingKey={downloadingKey}
                    onToggleSelection={toggleSelection}
                    onSelectVisible={selectVisible}
                    onDownloadFile={(item) => {
                      downloadOne(item).catch(() => undefined);
                    }}
                    onOpenLink={openRelatedLink}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {selectedItems.length > 0 && (
          <div
            className="flex shrink-0 flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
            aria-live="polite"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800">
                {selectedItems.length}{" "}
                {selectedItems.length === 1 ? "item" : "items"} selected
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                {selectedDownloadItems.length
                  ? `${selectedDownloadItems.length} ${
                      selectedDownloadItems.length === 1
                        ? "file is"
                        : "files are"
                    } ready to download across folders.`
                  : "Selected folders can be deleted if you have permission."}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedItems([])}
                disabled={isDownloadBusy || isDeleteBusy}
                className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-white hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear
              </button>
              {selectedDownloadItems.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    downloadSelected().catch(() => undefined);
                  }}
                  disabled={isDownloadBusy || isDeleteBusy}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {bundleDownloadMutation.isLoading ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  )}
                  Download {selectedDownloadItems.length > 1 ? "ZIP" : "file"}
                </button>
              )}
              {canUpdateDocuments &&
                selectedMovableFileIds.length === selectedItems.length && (
                  <button
                    type="button"
                    onClick={() => setMoveDialogOpen(true)}
                    disabled={isDownloadBusy || isDeleteBusy || isMoveBusy}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FolderArrowDownIcon className="h-4 w-4" />
                    Move
                  </button>
                )}
              {canUpdateDocuments && selectedDeleteItems.length > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    requestDelete(
                      selectedItems.filter((item) =>
                        selectionToDeleteItem(item)
                      )
                    )
                  }
                  disabled={isDownloadBusy || isDeleteBusy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete {selectedDeleteItems.length}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <DocumentFolderDialog
        open={folderDialogOpen}
        companyId={companyId}
        parentId={folderId}
        targetBreadcrumb={targetBreadcrumb}
        onClose={() => setFolderDialogOpen(false)}
        onCreated={reloadCurrentFolder}
      />
      <DocumentUploadDialog
        open={uploadDialogOpen}
        companyId={companyId}
        folderId={folderId}
        targetBreadcrumb={targetBreadcrumb}
        onClose={() => setUploadDialogOpen(false)}
        onUploadsCompleted={reloadCurrentFolder}
      />
      <DeleteConfirmationDialog
        target={deleteTarget}
        isDeleting={isDeleteBusy}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          confirmDelete().catch(() => undefined);
        }}
      />
      <Transition.Root show={moveDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            if (!isMoveBusy) setMoveDialogOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/30" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto p-4">
            <div className="flex min-h-full items-center justify-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-2 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-2 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
                  <Dialog.Title className="text-base font-semibold text-gray-900">
                    Move {selectedMovableFileIds.length} {selectedMovableFileIds.length === 1 ? "file" : "files"}
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose the current folder, a subfolder, or the company root.
                  </p>
                  <div className="mt-4 space-y-2">
                    <button
                      type="button"
                      onClick={() => moveSelectedFiles(null).catch(() => undefined)}
                      disabled={isMoveBusy}
                      className="flex w-full items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FolderIcon className="h-5 w-5 text-amber-500" />
                      Company Documents (root)
                    </button>
                    {folderId && (
                      <button
                        type="button"
                        onClick={() => moveSelectedFiles(folderId).catch(() => undefined)}
                        disabled={isMoveBusy}
                        className="flex w-full items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <FolderIcon className="h-5 w-5 text-amber-500" />
                        {currentPath}
                      </button>
                    )}
                    {browser?.folders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => moveSelectedFiles(folder.id).catch(() => undefined)}
                        disabled={isMoveBusy}
                        className="flex w-full items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <FolderIcon className="h-5 w-5 text-amber-500" />
                        {folder.name}
                      </button>
                    ))}
                  </div>
                  <div className="mt-5 flex justify-start">
                    <button
                      type="button"
                      onClick={() => setMoveDialogOpen(false)}
                      disabled={isMoveBusy}
                      className="rounded-sm border border-primary px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/10 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default DocumentVoultView;
